import React, { useState, useEffect, useMemo, useCallback } from 'react';
import usePhonesManage from '@/hooks/usePhonesManage';
import { message, Modal, Input, Table, Pagination } from 'starry-sky-ui';
import type { ColumnType } from 'starry-sky-ui';
import type { PhoneItem } from '@/types/admin';
import './index.scss';

const PHONE_REGEX = /^1[3-9]\d{9}$/;

const Phones: React.FC = () => {
  const { data: allPhones, loading, addPhone, deletePhone } = usePhonesManage();

  // ---- 搜索 ----
  const [keyword, setKeyword] = useState('');

  const filteredPhones = useMemo(() => {
    if (!keyword.trim()) return allPhones;
    const kw = keyword.trim().toLowerCase();
    return allPhones.filter(
      (p) =>
        String(p.id ?? '').toLowerCase().includes(kw) ||
        p.phone.toLowerCase().includes(kw),
    );
  }, [allPhones, keyword]);

  // ---- 分页 ----
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const total = filteredPhones.length;
  const pagedPhones = useMemo(
    () => filteredPhones.slice((page - 1) * pageSize, page * pageSize),
    [filteredPhones, page, pageSize],
  );

  const handlePageChange = useCallback((p: number, ps: number) => {
    setPage(p);
    setPageSize(ps);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [keyword]);

  // ---- 新增弹窗 ----
  const [modalOpen, setModalOpen] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const openAdd = useCallback(() => {
    setPhoneValue('');
    setPhoneError('');
    setModalOpen(true);
  }, []);

  const handlePhoneChange = useCallback((val: string) => {
    // 只允许数字
    const digits = val.replace(/\D/g, '');
    setPhoneValue(digits);
    if (phoneError) setPhoneError('');
  }, [phoneError]);

  const validatePhone = (phone: string): string => {
    if (!phone) return '请输入手机号';
    if (!PHONE_REGEX.test(phone)) return '请输入正确的11位手机号';
    return '';
  };

  const handleSubmit = useCallback(async () => {
    const err = validatePhone(phoneValue);
    if (err) {
      setPhoneError(err);
      return;
    }

    setSubmitting(true);
    try {
      await addPhone(phoneValue);
      message.success('手机号添加成功！');
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  }, [phoneValue, addPhone]);

  // ---- 删除确认 ----
  const [deleteTarget, setDeleteTarget] = useState<PhoneItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deletePhone(deleteTarget.id!);
      message.success('手机号删除成功！');
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, deletePhone]);

  // ---- 列定义 ----
  const columns: ColumnType[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 200,
      render: (val: string) => <span className="phone-cell-value">{val}</span>,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: unknown, record: PhoneItem) => (
        <div className="phone-cell-actions">
          <button
            className="phone-action-btn delete"
            onClick={() => setDeleteTarget(record)}
          >
            删除
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="phones-container">
      <div className="phones-header">
        <h1 className="phones-title">允许登录手机号管理</h1>
        <button className="phones-add-btn" onClick={openAdd}>
          + 新增手机号
        </button>
      </div>

      {/* 搜索栏 */}
      <div className="phones-filters">
        <Input
          placeholder="搜索 ID / 手机号..."
          value={keyword}
          onChange={setKeyword}
          allowClear
          size="middle"
          className="phones-search-input"
        />
        <span className="phones-count">共 {total} 个手机号</span>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={pagedPhones}
        rowKey={(record: PhoneItem) => record.id!.toString()}
        loading={loading}
        size="middle"
        bordered
        pagination={false}
        emptyText="暂无手机号"
      />

      {/* 分页 */}
      {total > 0 && (
        <div className="phones-pagination-wrap">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showTotal
            showSizeChanger
            pageSizeOptions={[10, 20, 30, 50]}
            onChange={handlePageChange}
          />
        </div>
      )}

      {/* 新增弹窗 */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="新增允许登录手机号"
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        okText="确定"
        cancelText="取消"
      >
        <div className="phone-modal-body">
          <div className="phone-modal-field">
            <label className="phone-modal-label">手机号</label>
            <Input
              value={phoneValue}
              onChange={handlePhoneChange}
              placeholder="请输入11位手机号"
              maxLength={11}
            />
            {phoneError && <span className="phone-modal-error">{phoneError}</span>}
          </div>
        </div>
      </Modal>

      {/* 删除确认弹窗 */}
      <Modal
        open={deleteTarget !== null}
        title="确认删除"
        onOk={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        onClose={() => setDeleteTarget(null)}
        confirmLoading={deleteLoading}
        okText="删除"
        cancelText="取消"
      >
        <p>确定要删除手机号「{deleteTarget?.phone}」吗？</p>
      </Modal>
    </div>
  );
};

export default Phones;